import React, { useState, useEffect } from 'react';

function extractYouTubeVideoId(urlOrId: string) {
  if (!urlOrId) return '';
  if (/^[\w-]{11}$/.test(urlOrId)) return urlOrId;
  const regex = /(?:youtube.com\/(?:[^/\n\s]+\/\S+\/|(?:v|embed)\/|.*[?&]v=)|youtu.be\/)([\w-]{11})/;
  const match = urlOrId.match(regex);
  return match ? match[1] : '';
}

interface WebinarVideoModalProps {
  videoType: 'youtube' | 'mux';
  videoId: string;
  onClose: () => void;
}

const WebinarVideoModal: React.FC<WebinarVideoModalProps> = ({ videoType, videoId, onClose }) => {
  const cleanVideoId = videoType === 'youtube' ? extractYouTubeVideoId(videoId) : videoId;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
      }}
    >
      <div
        style={{
          background: '#fff',
          padding: '1rem',
          borderRadius: '8px',
          maxWidth: '90%',
          minWidth: '50%',
          maxHeight: '90%',
          position: 'relative',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            fontSize: '1.5rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
          aria-label='Close modal'
        >
          ×
        </button>
        {videoType === 'youtube' ? (
          <iframe
            width='100%'
            height='400'
            src={`https://www.youtube.com/embed/${cleanVideoId}`}
            title='YouTube video player'
            frameBorder='0'
            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
            allowFullScreen
          />
        ) : (
          <iframe
            src={`https://player.mux.com/${videoId}`}
            width='100%'
            height='400'
            allow='autoplay; fullscreen'
            allowFullScreen
            title='Mux video player'
          />
        )}
      </div>
    </div>
  );
};

function useDeviceType() {
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  useEffect(() => {
    const updateDevice = () => {
      if (window.innerWidth <= 768) setDevice('mobile');
      else if (window.innerWidth <= 1024) setDevice('tablet');
      else setDevice('desktop');
    };
    updateDevice();
    window.addEventListener('resize', updateDevice);
    return () => window.removeEventListener('resize', updateDevice);
  }, []);

  return device;
}

function getResponsiveValue<T extends string | number>(
  value: T | { desktop?: T; tablet?: T; mobile?: T } | undefined,
  device: 'desktop' | 'tablet' | 'mobile',
): T | undefined {
  if (!value) return undefined;
  if (typeof value === 'string' || typeof value === 'number') return value;
  return value[device];
}

interface WebinarCard {
  thumbnail: string;
  videoId: string;
  videoType: 'youtube' | 'mux';
  title: string;
  description: string;
  date: string;
  category?: string;
}

interface ResponsiveValue {
  desktop?: string;
  tablet?: string;
  mobile?: string;
}

interface WebinarGridProps {
  cards: WebinarCard[];
  titleFontSize?: string | ResponsiveValue;
  titleColor?: string | ResponsiveValue;
  descriptionFontSize?: string | ResponsiveValue;
  descriptionColor?: string | ResponsiveValue;
  dateFontSize?: string | ResponsiveValue;
  dateColor?: string | ResponsiveValue;
  dateBgColor?: string | ResponsiveValue;
  loadMoreFontSize?: string | ResponsiveValue;
  loadMoreTextColor?: string | ResponsiveValue;
  loadMoreBgColor?: string | ResponsiveValue;
  filterFontSize?: string | ResponsiveValue;
  filterButtonPadding?: string | ResponsiveValue;
  filterActiveColor?: string | ResponsiveValue;
  filterInactiveColor?: string | ResponsiveValue;
  filterActiveTextColor?: string | ResponsiveValue;
  filterInactiveTextColor?: string | ResponsiveValue;
  filterBorderRadius?: string | ResponsiveValue;
}

const WebinarGridComponent: React.FC<WebinarGridProps> = ({
  cards = [],
  titleFontSize,
  titleColor,
  descriptionFontSize,
  descriptionColor,
  dateFontSize,
  dateColor,
  dateBgColor = '#FF6319',
  loadMoreFontSize,
  loadMoreTextColor,
  loadMoreBgColor = '#FF6319',
  filterFontSize,
  filterButtonPadding,
  filterActiveColor = '#FF6319',
  filterInactiveColor = '#F5F5F5',
  filterActiveTextColor = '#FFFFFF',
  filterInactiveTextColor = '#333333',
  filterBorderRadius = '6px',
}) => {
  const [showModal, setShowModal] = useState<null | { type: string; id: string }>(null);
  const [visibleCount, setVisibleCount] = useState(9);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const device = useDeviceType();

  const categories = Array.from(new Set(cards.map((card) => card.category).filter(Boolean))) as string[];

  const filteredCards = selectedCategory ? cards.filter((card) => card.category === selectedCategory) : cards;

  const visibleCards = filteredCards.slice(0, visibleCount);

  const resolvedFilterFontSize = getResponsiveValue(filterFontSize, device) || '1.375rem';
  const resolvedFilterPadding = getResponsiveValue(filterButtonPadding, device) || '0.75rem 2rem';
  const resolvedFilterActiveColor = getResponsiveValue(filterActiveColor, device) || '#FF6319';
  const resolvedFilterInactiveColor = getResponsiveValue(filterInactiveColor, device) || '#F5F5F5';
  const resolvedFilterActiveTextColor = getResponsiveValue(filterActiveTextColor, device) || '#FFFFFF';
  const resolvedFilterInactiveTextColor = getResponsiveValue(filterInactiveTextColor, device) || '#333333';
  const resolvedFilterBorderRadius = getResponsiveValue(filterBorderRadius, device) || '6px';

  return (
    <div style={{ width: '100%', padding: '2rem' }}>
      {categories.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.75rem',
            marginBottom: '2rem',
          }}
        >
          <button
            onClick={() => {
              setSelectedCategory(null);
              setVisibleCount(9);
            }}
            style={{
              padding: resolvedFilterPadding,
              fontSize: resolvedFilterFontSize,
              border: 'none',
              borderRadius: resolvedFilterBorderRadius,
              backgroundColor: !selectedCategory ? resolvedFilterActiveColor : resolvedFilterInactiveColor,
              color: !selectedCategory ? resolvedFilterActiveTextColor : resolvedFilterInactiveTextColor,
              cursor: 'pointer',
              fontWeight: !selectedCategory ? 'bold' : 'normal',
            }}
          >
            All
          </button>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => {
                setSelectedCategory(category);
                setVisibleCount(9);
              }}
              style={{
                padding: resolvedFilterPadding,
                fontSize: resolvedFilterFontSize,
                border: 'none',
                borderRadius: resolvedFilterBorderRadius,
                backgroundColor:
                  selectedCategory === category ? resolvedFilterActiveColor : resolvedFilterInactiveColor,
                color: selectedCategory === category ? resolvedFilterActiveTextColor : resolvedFilterInactiveTextColor,
                cursor: 'pointer',
                fontWeight: selectedCategory === category ? 'bold' : 'normal',
              }}
            >
              {category}
            </button>
          ))}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1.5rem',
        }}
      >
        {visibleCards.map((card, idx) => (
          <div
            key={idx}
            style={{
              background: '#fff',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
              cursor: 'default',
            }}
          >
            <div
              style={{ position: 'relative', cursor: 'pointer' }}
              onClick={() => setShowModal({ type: card.videoType, id: card.videoId })}
            >
              <img
                src={card.thumbnail}
                alt={card.title}
                style={{ width: '100%', height: '200px', objectFit: 'cover' }}
              />
              <svg
                width='64'
                height='64'
                viewBox='0 0 50 50'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none',
                  filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.7))',
                }}
              >
                <path
                  d='M10.3442 10.1865C10.5577 7.13019 14.0199 5.13123 16.9546 6.72461V6.72559L43.647 21.2422L43.6548 21.2471L43.6636 21.251C44.3437 21.6127 44.9123 22.1532 45.3091 22.8135C45.7058 23.4738 45.9155 24.2296 45.9155 25C45.9155 25.7704 45.7058 26.5262 45.3091 27.1865C44.9123 27.8468 44.3437 28.3873 43.6636 28.749L43.6548 28.7529L43.647 28.7578L16.9556 43.2725H16.9546C13.9262 44.921 10.3335 42.7383 10.3335 39.5166V10.4854L10.3442 10.1865Z'
                  fill='#FF6319'
                  stroke='white'
                  strokeWidth='4'
                />
              </svg>
            </div>

            <div style={{ padding: '1.25rem' }}>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: getResponsiveValue(dateFontSize, device) || '1.375rem',
                  color: getResponsiveValue(dateColor, device) || '#fff',
                  backgroundColor: getResponsiveValue(dateBgColor, device) || '#FF6319',
                  padding: '0.3rem 0.6rem',
                  borderRadius: '4px',
                  marginBottom: '0.75rem',
                }}
              >
                {card.date}
              </span>

              <h3
                style={{
                  fontSize: getResponsiveValue(titleFontSize, device) || '2.25rem',
                  color: getResponsiveValue(titleColor, device) || '#000',
                  margin: '0 0 0.75rem 0',
                  lineHeight: '1.3',
                }}
              >
                {card.title}
              </h3>

              <p
                style={{
                  fontSize: getResponsiveValue(descriptionFontSize, device) || '1.5rem',
                  color: getResponsiveValue(descriptionColor, device) || '#666',
                  margin: 0,
                  lineHeight: '1.5',
                }}
              >
                {card.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {visibleCount < filteredCards.length && (
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button
            onClick={() => setVisibleCount((prev) => prev + 9)}
            style={{
              padding: '0.75rem 2rem',
              fontSize: getResponsiveValue(loadMoreFontSize, device) || '1.5rem',
              color: getResponsiveValue(loadMoreTextColor, device) || '#fff',
              backgroundColor: getResponsiveValue(loadMoreBgColor, device) || '#FF6319',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Load More
          </button>
        </div>
      )}

      {showModal && (
        <WebinarVideoModal
          videoType={showModal.type as 'youtube' | 'mux'}
          videoId={showModal.id}
          onClose={() => setShowModal(null)}
        />
      )}
    </div>
  );
};

export default WebinarGridComponent;
